// apps/client/src/game/systems/villagers.ts (M2-T11) — network-free villager playback (spec §9.13, §8.3 slot 'villagers'). Every client
// computes each villager's pose from the shared schedule (RoutineCache, the server's pose path) on a nav grid built once per town from
// generateTown(seed, { genVersion }) (D-40), so villager movement sends and receives nothing; only talk pauses arrive as events.
import { tuning } from '@whiskerton/content';
import type { MoveAnim, TownInfo, VillagerId, VillagerPublic } from '@whiskerton/shared';
import type { VillagerPose } from '@whiskerton/shared/villagers/schedule';
import type * as THREE from 'three';
import { IS_DEV } from '../../boot/env';
import { Avatar, type ActionAnim, type RigSource } from '../../characters/avatar';
import { bakeDoll } from '../../characters/doll';
import { villagerToDoll } from '../../characters/look';
import { villagerTag } from '../../characters/nametags';
import type { VillagerWorldReply, VillagerWorldRequest } from '../../characters/villagerWorker';
import { createVillagerWorld, RoutineCache, type ClientVillagerWorld } from '../../characters/villagers';
import { isWorldPatched, patchMaterial } from '../../engine/shader/worldPatch';
import { useGame } from '../../state/store';
import type { ExtSystem, Game } from '../game';

export interface VillagerAvatar { root: THREE.Object3D; setMove(a: MoveAnim, speed: number): void; play(a: ActionAnim): void; update(dt: number): void; dispose(): void }
type Deps = { avatar?: (v: VillagerPublic) => Promise<VillagerAvatar>; world?: (t: TownInfo) => Promise<ClientVillagerWorld> };
type Pose = VillagerPose & { homeAwake: boolean };
/** Idle anims as move clips. 'read' holds both hands up like a book (a looping action would outlast the next walk). */
const IDLE_MOVE: Record<string, MoveAnim> = { stand: 'idle', lookAround: 'idle', stretch: 'idle', sit: 'sit', fish: 'fish', read: 'hold' };

/** Every live villager avatar (outdoors here, indoors in their room, world/spaces/vroom.ts), so the talk screen can play a mood emote
 *  on whichever one is on screen. */
const tracked = new Map<VillagerId, Set<VillagerAvatar>>();
export function trackVillagerAvatar(id: VillagerId, av: VillagerAvatar): () => void {
  let set = tracked.get(id);
  if (!set) tracked.set(id, (set = new Set()));
  set.add(av);
  return () => { set.delete(av); if (set.size === 0 && tracked.get(id) === set) tracked.delete(id); };
}
/** Plays `anim` on the villager's visible avatars (none when they are off screen). */
export function villagerEmote(id: VillagerId, anim: ActionAnim): void {
  for (const av of tracked.get(id) ?? []) if (av.root.visible) av.play(anim);
}

/** A villager doll for the outdoors: every Avatar is baked into one skinned mesh (spec §8.7 budget), and this re-bake switches it to
 *  the world-patched bakedMaterial, so the doll bends with the curved world on the GPU; whatever is left (the contact blob) gets a
 *  patched copy of its material too, and the playback places villagers at their unbent height (residents are bent on the CPU, game.ts). */
export async function bakedVillagerAvatar(rig: Promise<RigSource>, v: VillagerPublic): Promise<Avatar> {
  const av = Avatar.create(await rig, villagerToDoll(v), tuning.move.residentHeight);
  bakeDoll(av.root);
  av.root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.visible || Array.isArray(m.material) || isWorldPatched(m.material)) return;
    m.material = m.material.clone();
    patchMaterial(m.material);
  });
  return av;
}

/** One generateTown + scheduleNav per town (D-40). generateTown alone is ~65–120 ms on a desktop and several times that on a phone, over
 *  the plan's 100 ms budget, so it runs in a module Worker (characters/villagerWorker.ts) while the town fades in. Without workers (tests,
 *  a WebView that refuses them) it runs here, in a macrotask during the enter-town fade. */
export function buildVillagerWorld(t: Pick<TownInfo, 'seed' | 'genVersion' | 'timeZone'>): Promise<ClientVillagerWorld> {
  const town = { seed: t.seed, genVersion: t.genVersion, timeZone: t.timeZone };
  const t0 = performance.now();
  const log = (where: string, ms: number) => {
    if (IS_DEV) console.info(`[villagers] world built in ${Math.round(ms)} ms (${where}; ${Math.round(performance.now() - t0)} ms to ready)`);
  };
  const here = () => new Promise<ClientVillagerWorld>((ok) => setTimeout(() => {
    const t1 = performance.now();
    const w = createVillagerWorld(town);
    log('main thread', performance.now() - t1);
    ok(w);
  }, 0));
  if (typeof Worker === 'undefined') return here();
  return new Promise((ok) => {
    let worker: Worker;
    try { worker = new Worker(new URL('../../characters/villagerWorker.ts', import.meta.url), { type: 'module' }); } catch { void here().then(ok); return; }
    worker.onmessage = (e: MessageEvent<VillagerWorldReply>) => { worker.terminate(); log('worker', e.data.ms); ok(e.data.world); };
    worker.onerror = (e) => { e.preventDefault(); worker.terminate(); void here().then(ok); };
    worker.postMessage(town satisfies VillagerWorldRequest);
  });
}

export class VillagerPlayback {
  private worldFor = '';
  private cache: RoutineCache | null = null;
  private gen = 0;
  private readonly views = new Map<VillagerId, { av: VillagerAvatar; tag: { remove(): void } | null; anim: string; untrack: () => void }>();
  private readonly pending = new Set<VillagerId>();
  private readonly failed = new Set<VillagerId>();
  private readonly poses = new Map<VillagerId, Pose>();
  private readonly game: Pick<Game, 'scenes' | 'assets'> & Partial<Pick<Game, 'you'>>;
  private readonly deps: Deps;
  constructor(game: Pick<Game, 'scenes' | 'assets'> & Partial<Pick<Game, 'you'>>, deps: Deps = {}) { this.game = game; this.deps = deps; }

  /** The villagers' world for this town, once per town (seed, genVersion); a different town drops every view first. */
  private ensureWorld(town: TownInfo): void {
    const key = `${town.id}:${town.seed}:${town.genVersion}`;
    if (this.worldFor === key) return;
    this.clear();
    this.worldFor = key; this.cache = null;
    const make = this.deps.world ?? buildVillagerWorld;
    void make(town).then((w) => { if (this.worldFor === key) this.cache = new RoutineCache(w); });
  }
  private avatarFor(v: VillagerPublic): Promise<VillagerAvatar> {
    if (this.deps.avatar) return this.deps.avatar(v);
    return bakedVillagerAvatar((this.game as Game).assets.rig(), v);
  }
  private drop(id: VillagerId): void {
    const view = this.views.get(id);
    if (!view) return;
    view.av.root.removeFromParent(); view.av.dispose(); view.tag?.remove(); view.untrack();
    this.views.delete(id);
    this.poses.delete(id);
  }
  /** Drops every villager view (leaving the town, or another town). Avatars still loading are dropped when they arrive. */
  private clear(): void {
    for (const id of [...this.views.keys()]) this.drop(id);
    this.poses.clear(); this.pending.clear(); this.failed.clear();
    this.gen++;
  }
  poseOf(id: VillagerId): Pose | null { return this.poses.get(id) ?? null; }

  update(nowMs: number, dt: number): void {
    const s = useGame.getState();
    if (!s.town || s.villagersList.length === 0) { if (this.views.size || this.poses.size) this.clear(); return; }
    this.ensureWorld(s.town);
    const out = this.game.scenes.out;
    const live = new Set(s.villagersList.map((v) => v.id));
    for (const id of [...this.views.keys()]) if (!live.has(id)) this.drop(id);
    for (const id of [...this.poses.keys()]) if (!live.has(id)) this.poses.delete(id);
    if (!this.cache || !out) return;
    const layer = out.layer('villagers');
    const me = this.game.you?.() ?? null;                         // only villagers near you are drawn (draw-call budget)
    for (const v of s.villagersList) {
      const pose = this.cache.poseFor(v, s.villagersPauses, nowMs);
      this.poses.set(v.id, pose);
      const view = this.views.get(v.id);
      if (!view) {
        if (!this.pending.has(v.id) && !this.failed.has(v.id)) this.spawn(v, layer);
        continue;
      }
      const near = !me || Math.hypot(pose.pos.x - me.pos.x, pose.pos.y - me.pos.y) <= tuning.villagers.renderRadius;
      view.av.root.visible = s.space === 'out' && pose.visible && near;
      if (!view.av.root.visible) continue;
      view.av.root.position.set(pose.pos.x, out.heightAt(pose.pos), pose.pos.y);
      view.av.root.rotation.y = pose.f;
      const anim = pose.anim === 'walk' ? 'walk' : `idle:${pose.anim}`;
      if (anim !== view.anim) {
        view.anim = anim;
        if (pose.anim === 'walk') view.av.setMove('walk', tuning.villagers.walkSpeed);
        else view.av.setMove(IDLE_MOVE[pose.anim] ?? 'idle', 0);
      }
      view.av.update(dt);
    }
  }
  private spawn(v: VillagerPublic, layer: THREE.Group): void {
    const gen = this.gen;
    this.pending.add(v.id);
    void this.avatarFor(v).then((av) => {
      if (gen !== this.gen || !useGame.getState().villagersList.some((x) => x.id === v.id)) { av.dispose(); return; }   // left meanwhile
      this.pending.delete(v.id);
      av.root.visible = false;                                    // until the next update places it
      layer.add(av.root);
      this.views.set(v.id, { av, tag: typeof document === 'undefined' ? null : villagerTag(av.root, v.name), anim: '', untrack: trackVillagerAvatar(v.id, av) });
    }, (e: unknown) => {
      if (gen !== this.gen) return;
      this.pending.delete(v.id);
      this.failed.add(v.id);                                      // never retried every frame; a new town (or reload) tries again
      if (IS_DEV) console.warn('villager avatar failed', v.id, e);
    });
  }
}

export const system: ExtSystem = {
  slot: 'villagers',
  create(game) {
    const playback = new VillagerPlayback(game);
    (game as Game & { villagerPlayback?: VillagerPlayback }).villagerPlayback = playback;   // read by the interaction and finders
    return { name: 'villagers', update: (f) => playback.update(f.now, f.dt) };
  },
};
export const playbackOf = (game: Game): VillagerPlayback | null => (game as Game & { villagerPlayback?: VillagerPlayback }).villagerPlayback ?? null;
